// Disabling the rule for inline styles since this animation heavily relies on CSS variables.
/* oxlint-disable react/forbid-component-props */
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { Box, Column, Stack } from '@shipt/design-system-layouts'
import { Display, Body } from '@shipt/design-system-typography'
import styled from 'styled-components'
import { screenSizes } from '@shipt/design-system-themes'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { objectStorageUrl } from '@/features/shared/utils/objectStorageUrl'
import { routes } from '@shared/constants/routes'
import { useEffect, useState } from 'react'
import { ButtonLink } from '@/features/shared/elements/Link/Buttons'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { keyframes } from '@/features/shared/styles/effects'
import { theme } from '@/features/shared/constants/shiptTheme'

type Props = { content_type_id: string; id: string }

const copy = 'Groceries and more, delivered from stores you love'.split(' ')

export const AnimatedHomepageHero = ({ content_type_id, id }: Props) => {
  const [hasViewportResized, setHasViewportResized] = useState(false)
  const { trackCMSElementViewed, trackCMSElementClicked } = useCMSTrackingEvents()

  useEffect(() => {
    trackCMSElementViewed({})
  }, [trackCMSElementViewed])

  useEffect(() => {
    if (hasViewportResized) {
      return
    }

    const handleResize = () => {
      setHasViewportResized(true)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [hasViewportResized])

  return (
    <HeroSection
      className={hasViewportResized ? 'is-resized' : ''}
      {...getCMSWrapperProps({ content_type_id, id })}
    >
      <HeroBackgroundWrapper className="fullWidth contentAtTablet">
        <HeroGrid
          direction={{ base: 'column-reverse', lg: 'row' }}
          padding={{
            base: ['xl', 'lg'],
            md: ['4xl', 'xxl'],
            lg: ['5xl', '3xl'],
          }}
          spacing={{ base: 'xl', md: 'xxl', lg: '3xl' }}
        >
          <MainTextColumn>
            <StaggeredLine
              style={{
                '--stagger-delay': '1.5s',
              }}
            >
              <Body size={{ base: 'md', md: 'lg' }} variant="secondary" as="h1">
                Same-day delivery
              </Body>
            </StaggeredLine>
            <Column spacing={{ base: 'md', md: 'xl' }}>
              <Display size="sm">
                {copy.map((segment, index) => (
                  <StaggeredLine
                    key={segment}
                    style={{
                      '--index': index,
                    }}
                  >
                    {segment + ' '}
                  </StaggeredLine>
                ))}
              </Display>

              <PopInWrapper>
                <Body size={{ base: 'xl', md: 'xxl' }} weight="medium">
                  With Shipt, it's never just an order. Get excellent same-day service – shopped in
                  the same way you would.
                </Body>
              </PopInWrapper>
              <StartShoppingButtonLink
                href={routes.GLOBAL_HOMEPAGE.url}
                onClick={() =>
                  trackCMSElementClicked({
                    content: 'start_shopping',
                    shelf_index: 0,
                    type: 'button',
                  })
                }
              >
                Start shopping
              </StartShoppingButtonLink>
            </Column>
          </MainTextColumn>

          <ImageGrid aria-hidden="true">
            <HandDeviceWrapper>
              <ContainImage
                src={objectStorageUrl('SKB_home.png')}
                alt="Sterling K. Brown holding a Shipt bag in front of produce."
                width={300}
                height={465}
                loading="eager"
                quality={100}
              />
            </HandDeviceWrapper>

            <AnimatedBagWrapper>
              <ContainImage
                src={objectStorageUrl('door-lifestyle.png')}
                alt="Hand holds a smartphone displaying nearby stores in the Shipt app."
                width={300}
                height={350}
                quality={100}
              />
            </AnimatedBagWrapper>

            <PreferredCardWrapper>
              <ContainImage
                src={objectStorageUrl('preferred-shopper-notification.png')}
                alt='App notification shows the message "Taylor will be there soon!"'
                width={300}
                height={75}
                quality={100}
              />
            </PreferredCardWrapper>
          </ImageGrid>
        </HeroGrid>
      </HeroBackgroundWrapper>
    </HeroSection>
  )
}

const HeroSection = styled(GridLayoutContainer)`
  --content-max-width: 1300px;
  --stagger-delay: 1.5s;
  --stagger-step: 0.02s;
  --hero-height-delay: 3.5s;
  --pop-delay: 2.5s;

  width: 100%;

  &.is-resized * {
    animation-duration: 0s !important;
    animation-delay: 0s !important;
  }

  @media ${screenSizes.tablet} {
    --hero-height-delay: 2.4s;
    --pop-delay: 3.3s;
  }
`

const HeroBackgroundWrapper = styled.div`
  position: relative;
  background-color: ${theme.color.background.base.neutral.subtle_100};
  overflow: hidden;

  @media ${screenSizes.tablet} {
    border-radius: 24px;
  }
`

const HeroGrid = styled(Stack)`
  min-height: 600px;
  align-items: center;
  text-align: center;
  --hero-height: 400px;
  animation: ${keyframes.homepageHeroHeight} 1s var(--hero-height-delay)
    ease-in-out forwards;

  @media ${screenSizes.tablet} {
    min-height: 750px;
    --hero-height: 520px;
  }

  @media (min-width: 1024px) {
    flex-direction: row;
    text-align: left;
  }

  @media (prefers-reduced-motion: reduce) {
    min-height: 400px;
    animation: none;

    @media ${screenSizes.smDesktop} {
      min-height: 520px;
    }
  }
`

const MainTextColumn = styled(Column)`
  text-wrap: balance;
  justify-content: center;

  @media (min-width: 1024px) {
    width: 40%;
  }

  @media ${screenSizes.only_tablet} {
    max-width: 80%;
  }
`

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto;
  gap: 16px;

  @media ${screenSizes.lgMobile} {
    gap: 24px;
  }

  @media ${screenSizes.tablet} {
    width: 60%;
  }

  min-height: 250px;
  position: relative;

  @media ${screenSizes.smDesktop} {
    gap: 40px;
    min-height: auto;
    position: static;
  }
`

const AnimatedBagWrapper = styled.div`
  z-index: 2;
  transform-origin: center top;
  opacity: 0;
  --bag-from-x: calc(-50% - 8px);
  --bag-from-scale: 1.3;
  --bag-mid-x: calc(-50% - 8px);
  animation: ${keyframes.homepageBagSequence} 2.5s 1s
    cubic-bezier(0.65, 0, 0.35, 1) forwards;

  @media (min-width: 1024px) {
    transform-origin: right top;
    transform: translate(calc(-120% - 40px), 0) scale(1.5);
    --bag-from-x: calc(-120% - 40px);
    --bag-from-scale: 1.5;
    --bag-mid-x: 0;
    animation: ${keyframes.homepageBagSequence} 2s 1s
      cubic-bezier(0.65, 0, 0.35, 1) forwards;
  }

  @media ${screenSizes.smDesktop} {
    transform: translate(calc(-110% - 40px), 0) scale(1.5);
    --bag-from-x: calc(-110% - 40px);
    animation: ${keyframes.homepageBagSequence} 2.5s 1s
      cubic-bezier(0.65, 0, 0.35, 1) forwards;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transform: none;
    position: static;
  }
`

const HandDeviceWrapper = styled.div`
  grid-row: 1 / -1;

  & > img {
    height: 100%;
  }

  z-index: 2;
  opacity: 0;
  animation: ${keyframes.enter} 0.8s ease-out 3s forwards;

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    animation: none;
  }
`

const PreferredCardWrapper = styled.div`
  z-index: 1;
  opacity: 0;

  --enter-scale: 0.8;
  animation: ${keyframes.enter} 0.6s 3s forwards;

  @media ${screenSizes.tablet} {
    --enter-y: -70%;
    --enter-scale: 0.4;
    animation: ${keyframes.enter} 0.7s
      cubic-bezier(0.175, 0.885, 0.32, 1.275) 2.3s forwards;
  }

  @media ${screenSizes.smDesktop} {
    --enter-x: -50%;
    --enter-y: 0;
    --enter-scale: 0.8;
    --enter-opacity: 0.4;
    animation: ${keyframes.enter} 0.7s cubic-bezier(0, 0, 0, 0) 3s forwards;
  }

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    animation: none;
  }
`

const StaggeredLine = styled.span`
  opacity: 0;
  --headline-from-bottom: -300%;
  animation: ${keyframes.homepageSlideInHeadline} 0.8s ease-out forwards;
  animation-delay: calc(
    var(--stagger-delay) + (var(--index, 0) * var(--stagger-step, 0))
  );
  position: relative;

  @media ${screenSizes.smDesktop} {
    --headline-from-bottom: 0;
    --headline-from-right: -100%;
    animation-duration: 0.5s;
  }

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    animation: none;
  }
`

const PopInWrapper = styled(Box)`
  opacity: 0;
  --enter-scale: 0.5;
  animation: ${keyframes.enter} 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  animation-delay: var(--pop-delay);

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    animation: none;
  }
`

const StartShoppingButtonLink = styled(ButtonLink)`
  align-self: center;
  width: 100%;
  opacity: 0;
  transform-origin: top left;
  --enter-scale: 0.5;
  animation: ${keyframes.enter} 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  animation-delay: var(--pop-delay);

  @media (min-width: 1024px) {
    align-self: start;
    width: auto;
  }

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    animation: none;
  }
`

const ContainImage = styled(Image)`
  display: block;
  width: 100%;
  height: auto;
`
